<!-- IdMeMfa.vue -->
<script lang="ts" setup>
    import { onMounted, computed, reactive, ref, nextTick } from 'vue';
    import { ChoiceApi, MessageSection } from '../../LibMain';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { timeout } from '../../lib/ChoiceUtils';
    import { DisplayStyle, TextDisplayStyle } from '../../Enums/UiEnums';
    import { MfaSetupModel, MfaEventStatusTypes, MfaSetupOptionTypes, IdMeMfaEventResponse } from '../../lib/Api/IdMe';
    import { popAlert } from '../../lib/Notification';
    import Card from '../../components/Card.vue'
    import LoadingSection from '../../components/LoadingSection.vue'
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import MessageText from '../../components/Messages/MessageText.vue';
    import MessageBadge from '../../components/Messages/MessageBadge.vue';
    import CountDown from '../../components/CountDown.vue';
    import TextInput from '../../components/TextInput.vue';

    const emits = defineEmits<{
        (e: "mfaSuccess"): void,
        (e: "mfaFailure", errorMsg?: "Issue starting challenge" | "Failure checking status" | "MFA request expired" | "Authorization denied"): void
    }>();

    const compData = reactive<{
        mfaSetups?: MfaSetupModel[],
        selectedIdMeMfaSetup?: MfaSetupModel,
        currentMfaEventResponse?: IdMeMfaEventResponse,
        challengeStarted?: boolean,

        //credentialInfo?: ICredentialInfoModal,
        mfaEventExpiration?: Date,
        errorMsg?: string,
        authenticatorCode?: string
    }>({});

    const idMeAuthAppLogoSrc = "/officemd/images/IdMe/IdMeAuthenticatorAppLogo.png";
    const authCodeInput = ref();
    const authCodeDigits = 6;

    onMounted(async () =>{
        compData.mfaSetups = await ChoiceApi.idMe.getMfaSetupsForEPCS(onErrorGettingMfaSetupForEPCS) ?? [];
    });

    async function onErrorGettingMfaSetupForEPCS(httpResp: Response, errorMessage?: string)
    {
        compData.errorMsg = errorMessage;
    }

    const mfaSetupCount = computed<number|undefined>(() => compData.mfaSetups?.length);

    // Legacy code action:
    //  await postData("/officemd/api/identrust/challenge", challenge)
    async function issueMfaChallenge(idMeMfaSetup: MfaSetupModel): Promise<void>
    {
        compData.challengeStarted = false;
        compData.selectedIdMeMfaSetup = idMeMfaSetup;

        try
        {
            switch(idMeMfaSetup.option)
            {
                case MfaSetupOptionTypes.push:
                    return await pushChallenge(idMeMfaSetup);
    
                case MfaSetupOptionTypes.generator:
                    return await codeGeneratorChallenge(idMeMfaSetup);
    
                default:
                    return;
            }
        }
        catch(err)
        {
            const errorMsg = (err as Error)?.message ?? "Unknown";
            await popAlert("An error occrued trying to issue the challenge: " + errorMsg);
            return;
        }
    }

    async function startMfaEvent(idMeMfaSetup: MfaSetupModel): Promise<IdMeMfaEventResponse|undefined>
    {
        compData.currentMfaEventResponse = undefined;
        const respMfaEventStatus = await ChoiceApi.idMe.startMfaEvent(idMeMfaSetup.uuid);
        if (!respMfaEventStatus)
        {
            await popAlert("Error starting MFA request");
            return;
        }
        compData.currentMfaEventResponse = respMfaEventStatus;
        compData.mfaEventExpiration = new Date(respMfaEventStatus.expires);
        return compData.currentMfaEventResponse;
    }

    async function pushChallenge(idMeMfaSetup: MfaSetupModel)
    {
        compData.selectedIdMeMfaSetup = idMeMfaSetup;
        
        await _pushChallenge(idMeMfaSetup);

        compData.selectedIdMeMfaSetup = undefined;
        compData.mfaEventExpiration = undefined;
        
    }

    // Handle a push notification challenge (return TRUE if approved)
    async function _pushChallenge(idMeMfaSetup: MfaSetupModel): Promise<boolean>
    {
        if (MfaSetupOptionTypes.push !== idMeMfaSetup.option)
        {
            await popAlert(`This method can only be used for ${idMeMfaSetup.option} notifications`);
            emits("mfaFailure");
            return false;
        }

        compData.challengeStarted = false;        
        const eventResp = await startMfaEvent(idMeMfaSetup);
        compData.challengeStarted = true;
        if (!eventResp)
        {
            emits("mfaFailure", "Issue starting challenge");
            return false;
        }

        while(true)
        {
            const respCurrentMfaEventStatus = await ChoiceApi.idMe.checkMfaEventStatus(eventResp.uuid);
            if (!respCurrentMfaEventStatus)
            {
                emits("mfaFailure", "Failure checking status");
                return false;
            }

            if (respCurrentMfaEventStatus?.status === MfaEventStatusTypes.accepted)
            {
                emits("mfaSuccess");                
                return true;
            }

            if (respCurrentMfaEventStatus?.status === MfaEventStatusTypes.failed)
            {
                emits("mfaFailure", "Authorization denied");
                return false;
            }

            // Are we at the end of the event window?
            if ((new Date()).toISOString() > respCurrentMfaEventStatus?.expires)
            {
                emits("mfaFailure", "MFA request expired");
                return false;
            }

            // Pause between attempts
            await timeout(1000);
        }
    }

    async function codeGeneratorChallenge(idMeMfaSetup: MfaSetupModel): Promise<void>
    {
        if (MfaSetupOptionTypes.generator !== idMeMfaSetup.option)
        {
            await popAlert(`This method can only be used for ${idMeMfaSetup.option} notifications`);
            return;
        }

        const eventResp = await startMfaEvent(idMeMfaSetup);
        if (!eventResp)
        {
            emits("mfaFailure", "Issue starting challenge");
            return;
        }

        compData.challengeStarted = true;            
        compData.authenticatorCode = undefined;

        await nextTick();
        authCodeInput.value.focus();
    }

    async function cancelMfaEvent()
    {
        compData.selectedIdMeMfaSetup = undefined;
        emits("mfaFailure");
    }

    async function verifyAuthCode(): Promise<void>
    {
        if (!compData.currentMfaEventResponse?.uuid || !compData.authenticatorCode)
        {
            alert("Neither compData.currentMfaEventResponse.uuid nor compData.authenticatorCode can be undefined");
            return;
        }
        const resp = await ChoiceApi.idMe.checkMfaEventCode(compData.currentMfaEventResponse?.uuid, compData.authenticatorCode);
        if (!resp)
        {
            popAlert("Error verifying authentication code");
            return;
        }

        if (resp.status === MfaEventStatusTypes.accepted)
        {
            emits("mfaSuccess");
            compData.currentMfaEventResponse = undefined;
            compData.selectedIdMeMfaSetup = undefined;
            return;
        }

        compData.authenticatorCode = undefined;
        await nextTick();
        authCodeInput.value.focus();
        await popAlert("Invalid code");
    }

    function eventExpired()
    {
        compData.currentMfaEventResponse = undefined;
        emits("mfaFailure");
    }

    const isCodeAuthenticationOption = computed(() => {
        switch(compData.selectedIdMeMfaSetup?.option)
        {
            case MfaSetupOptionTypes.generator:
            case MfaSetupOptionTypes.phone:
                return true;

            default:
                return false;
        }
    });

    const authenticatorCodeTextBoxMessage = computed<string|undefined>(() => 
        {            
            const authCodeLen = compData.authenticatorCode?.length ?? 0;
            if (0 === authCodeLen)
                return "Please enter the authenticator application code (above)";
            if (authCodeLen < authCodeDigits)
                return `Authenticator application code is too short.  Expected ${authCodeDigits} digits.`;
            if (authCodeLen > authCodeDigits)
                return `Authenticator application code is too long.  Expected ${authCodeDigits} digits.`;
        });
</script>

<style scoped>
    .IdMeAppLogo{
        height: 2em;
    }
</style>

<template>    
    <template v-if="undefined === mfaSetupCount">
        <LoadingSection :isLoading="true" loadingMsg="Getting ID.me info"/>
    </template>

    <template v-if="compData.errorMsg">
        <MessageSection :displayStyle="DisplayStyle.Danger">
            {{ compData.errorMsg }}
        </MessageSection>
    </template>

    <template v-if="0 === mfaSetupCount">
        <MessageSection>
            There are no ID.me MFA setups available for EPCS
        </MessageSection>
    </template>

    <template v-if="mfaSetupCount && mfaSetupCount > 0">
        <div v-if="!compData.selectedIdMeMfaSetup">
            <Card v-for="setup in compData.mfaSetups" class="mb-2" :headerDisplayStyle="DisplayStyle.Secondary">
                <template #title>
                    {{ setup.label }}
                </template>
                <div>
                    {{ setup.name }}
                </div>
                <ActionButtonV2 :action="ActionButtonStyle.Send" :performAction="async () => await issueMfaChallenge(setup)" />
                <span v-if="setup.option === MfaSetupOptionTypes.push">
                    <img :src="idMeAuthAppLogoSrc" alt="ID.me application logo" class="IdMeAppLogo"/> A message will be sent to the ID.me application
                </span>
                <span v-if="setup.option === MfaSetupOptionTypes.generator">
                    <img :src="idMeAuthAppLogoSrc" alt="ID.me application logo" class="IdMeAppLogo"/> Use the ID.me application to get the <b>{{authCodeDigits}} digit code</b>
                </span>                
            </Card>

            <ActionButtonV2 :action="ActionButtonStyle.Close" text="Cancel" :performAction="cancelMfaEvent" :displayStyle="DisplayStyle.Danger" />
        </div>

        <div v-if="compData.selectedIdMeMfaSetup">
            <div v-if="MfaSetupOptionTypes.push === compData.selectedIdMeMfaSetup?.option">
                <div v-if="compData.challengeStarted">                
                    <div>
                        <img :src="idMeAuthAppLogoSrc" alt="ID.me application logo" class="IdMeAppLogo"/>
                        MFA Challenge pushed to: <b>{{ compData.selectedIdMeMfaSetup.name }}</b> using ID.me application
                    </div>
    
                    <MessageBadge>
                        Awaiting a reply
                    </MessageBadge>
                    <div class="mt-2">
                    </div>
                </div>
            </div>

            <div v-if="isCodeAuthenticationOption">
                <div v-if="compData.challengeStarted">
                    <img :src="idMeAuthAppLogoSrc" alt="ID.me application logo" class="IdMeAppLogo"/>
                    Enter the <b>{{authCodeDigits}} digit code</b> displayed in the ID.me authenticator application code on: <b>{{ compData.selectedIdMeMfaSetup.name }}</b>
                    <div class="mb-2 mt-1">
                        <TextInput v-model:textValue="compData.authenticatorCode" ref="authCodeInput" />
                    </div>
                    <span class="me-1">
                        <template v-if="!authenticatorCodeTextBoxMessage">
                            <ActionButtonV2 :action="ActionButtonStyle.Send" :performAction="verifyAuthCode" />
                        </template>
                        <template v-if="authenticatorCodeTextBoxMessage">
                            <MessageText :displayStyle="DisplayStyle.Danger">{{authenticatorCodeTextBoxMessage}}</MessageText>
                        </template>
                    </span>
                </div>
            </div>
            <div v-if="!compData.challengeStarted">
                <LoadingSection :isLoading="true" loadingMsg="Sending challenge request"/>
            </div>

            <!-- Show remaining time -->
            <MessageText :displayStyle="TextDisplayStyle.Muted">
                <em>    
                    Time remaining: <CountDown :expires="compData.mfaEventExpiration" @expired="eventExpired"/>
                </em>
            </MessageText>

            <div>
                <ActionButtonV2 :action="ActionButtonStyle.Blank" :performAction="cancelMfaEvent" :displayStyle="DisplayStyle.Danger">
                    Cancel
                </ActionButtonV2>
            </div>
        </div>
    </template>
</template>